// import components
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@sdb/planning-ui';
import PlanGroupsDialog from 'components/shared/PlanGroups/PlanGroupsDialog';
import ArrowNavigation from '../ArrowNavigation';
import PlanUnitSelectorMulti from './components/PlanUnitSelectorMulti';
import PlanUnitSelectorSingle from './components/PlanUnitSelectorSingle';

// import hooks
import { usePlanUnitsOptions } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePlanUnitSearch,
  usePlanUnitSelection
} from './PlanUnitSelector.hooks';

// import utils
import classNames from 'classnames';
import { isEqual } from 'date-fns';
import { getMinDate } from 'utils/date';
import { getDefaultInterval } from './util';

// import types
import type { PlanUnitMix } from 'components/types';
import type { PlanUnitSelectorProps } from './PlanUnitSelector.types';

// import styles
import {
  StyledPlanUnitSelectorBox,
  StyledToolbarDivider
} from './PlanUnitSelector.styles';

export default function PlanUnitSelector<T extends boolean, U extends boolean>(
  props: PlanUnitSelectorProps<T, U>
) {
  'use memo';

  const {
    className,
    defaultValue,
    onSelected,
    onChange: fieldOnChange,
    doNotPreselectOnlyOption,
    firstOptionIsDefault,
    interval: intervalProp,
    tempOptions,
    useEmployeePlanunits = false,
    usePublicPlanUnits,
    fullWidth,
    hideNavigation,
    hideGroups,
    doNotPersist,
    isSingleSelect,
    disabled,
    filterByType,
    error,
    helperText,
    onClear,
    variant = 'standard',
    size = 'small',
    showChips = false,
    setPlanUnits,
    selectedValues,
    isToolbar = false,
    isVertical = false
  } = props;

  const { t } = useTranslation('common');
  const interval = intervalProp || getDefaultInterval();
  const compareInterval = isEqual(interval.startDate, getMinDate())
    ? getDefaultInterval()
    : interval;

  const options = usePlanUnitsOptions({
    showGroups: !hideGroups,
    useEmployeePlanunits,
    usePublicPlanUnits,
    interval,
    filterByType
  });

  const search = usePlanUnitSearch();

  const { selectedOptions, onChange } = usePlanUnitSelection({
    options,
    onSelected,
    onChange: fieldOnChange,
    defaultValue,
    tempOptions,
    selectedValues,
    doNotPersist,
    doNotPreselectOnlyOption,
    firstOptionIsDefault,
    shouldSaveCCPreferenceKey: props.shouldSaveCCPreferenceKey,
    lastUsedCCPreferenceKey: props.lastUsedCCPreferenceKey
  });

  const [showPlanGroupsDialog, setShowPlanGroupsDialog] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const isMulti = selectedOptions.length > 1 || isOpen;

  const onArrowNavigate = (direction, currentIndex) => {
    let nextOption: PlanUnitMix;
    if (direction === 'prev') {
      nextOption = options[currentIndex - 1];
    } else {
      nextOption = selectedOptions[0] ? options[currentIndex + 1] : options[0];
      if ('costCenters' in nextOption) {
        const startIndex = selectedOptions[0] ? currentIndex + 1 : 0; // skip plangroups
        const planUnit = options.find(
          (o, i) => i > startIndex && !('costCenters' in o)
        );
        if (planUnit) {
          nextOption = planUnit;
        }
      }
    }
    onChange(null, [nextOption]);
  };

  useEffect(() => {
    if (setPlanUnits) {
      setPlanUnits(options);
    }
  }, [options, setPlanUnits]);

  const branchProps = {
    options,
    selectedOptions,
    onChange,
    compareInterval,
    fullWidth,
    disabled,
    error,
    helperText,
    variant,
    isToolbar,
    onClear
  };

  return (
    <StyledPlanUnitSelectorBox
      className={classNames(className, 'planunit-selector', {
        'full-width': fullWidth
      })}
      isToolbar={isToolbar}
    >
      {isMulti ? (
        <PlanUnitSelectorMulti
          {...branchProps}
          isOpen={isOpen}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          search={search}
          showChips={showChips}
        />
      ) : (
        <PlanUnitSelectorSingle
          {...branchProps}
          isSingleSelect={isSingleSelect}
          onAddMore={() => setOpen(true)}
          size={size}
        />
      )}
      {(!hideNavigation || !hideGroups) && !disabled && (
        <div className="d-flex v-center">
          <ArrowNavigation
            onNavigate={onArrowNavigate}
            options={options}
            selectedOption={selectedOptions[0]}
            disabled={isMulti}
            showCnt={!props.hideNavigation && props.showNavigationCnt}
            isToolbar={isToolbar}
          />
          {!hideGroups && (
            <Tooltip title={<span>{t('editPlanGroups')}</span>}>
              <IconButton
                color={isToolbar ? 'inherit' : 'primary'}
                onClick={() => setShowPlanGroupsDialog(true)}
                className="edit-plangroups"
                size="small"
                disabled={disabled}
              >
                <Icon type="folder-plus" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
      {showPlanGroupsDialog && (
        <PlanGroupsDialog onHidden={() => setShowPlanGroupsDialog(false)} />
      )}
      {isToolbar && !isVertical && (
        <StyledToolbarDivider
          orientation="vertical"
          variant="middle"
          flexItem
        />
      )}
    </StyledPlanUnitSelectorBox>
  );
}
